import type { Maybe } from '@adornis/base/utilTypes.js';
import { DateTime, Interval } from 'luxon';

/**
 * A school year runs from the 1. of August until the 31. of July of the following calendar year and is
 * labelled with the last two digits of both years, e.g. `SJ 25/26` or `BuT SJ 25/26`.
 *
 * This module is the single source of truth for that turning point. Do not inline the date anywhere else --
 * the whole point is that nothing has to be touched again when a school year rolls over.
 *
 * NOTE: A Förderjahr (`FJ 25/26`) is a different thing with a different turning point (1. May - 30. April).
 * See {@link getIntervalsForSchoolYearString} in master-data-management/partner. Keep the two apart.
 */
export const SCHOOL_YEAR_START_MONTH = 8;
export const SCHOOL_YEAR_START_DAY = 1;

/** Label prefixes in use. BuT school years are plain school years, they just carry their own prefix. */
export type SchoolYearPrefix = 'SJ' | 'BuT SJ';

/** The prefix all BuT year labels carry, e.g. `BuT SJ 25/26`. */
export const BUT_SCHOOL_YEAR_PREFIX = 'BuT SJ' satisfies SchoolYearPrefix;

/**
 * The calendar year the school year containing `at` starts in.
 * 2026-01-15 -> 2025, 2026-08-01 -> 2026.
 */
export function schoolYearStartYear(at: DateTime = DateTime.now()): number {
  const turningPoint = at.set({ month: SCHOOL_YEAR_START_MONTH, day: SCHOOL_YEAR_START_DAY }).startOf('day');
  return at < turningPoint ? at.year - 1 : at.year;
}

/**
 * The half open interval [1. August startYear, 1. August startYear + 1) covering the whole school year.
 * Half open on purpose so that the 31. of July belongs to a school year for its entire duration.
 */
export function schoolYearInterval(startYear: number): Interval {
  const start = DateTime.fromObject({
    year: startYear,
    month: SCHOOL_YEAR_START_MONTH,
    day: SCHOOL_YEAR_START_DAY,
  }).startOf('day');
  return Interval.fromDateTimes(start, start.plus({ years: 1 }));
}

function twoDigits(year: number): string {
  return (year % 100).toString().padStart(2, '0');
}

/** Builds the label for the school year starting in `startYear`, e.g. 2025 -> `BuT SJ 25/26`. */
export function schoolYearLabelForStartYear(prefix: SchoolYearPrefix, startYear: number): string {
  return `${prefix} ${twoDigits(startYear)}/${twoDigits(startYear + 1)}`;
}

/** The label of the school year that is running at `at`. */
export function currentSchoolYearLabel(prefix: SchoolYearPrefix, at: DateTime = DateTime.now()): string {
  return schoolYearLabelForStartYear(prefix, schoolYearStartYear(at));
}

/** The label of the school year before the one running at `at`. */
export function previousSchoolYearLabel(prefix: SchoolYearPrefix, at: DateTime = DateTime.now()): string {
  return schoolYearLabelForStartYear(prefix, schoolYearStartYear(at) - 1);
}

/** The label of the school year after the one running at `at`. */
export function nextSchoolYearLabel(prefix: SchoolYearPrefix, at: DateTime = DateTime.now()): string {
  return schoolYearLabelForStartYear(prefix, schoolYearStartYear(at) + 1);
}

/**
 * Reads the starting calendar year out of a label. Accepts anything containing `nn/nn`, so it works
 * regardless of the prefix and tolerates the legacy labels already stored in the database.
 * Returns null if the label does not contain a school year at all.
 */
export function parseSchoolYearStartYear(label: Maybe<string>): Maybe<number> {
  if (!label) return null;
  const match = /(\d{2})\s*\/\s*(\d{2})/.exec(label);
  if (!match?.[1]) return null;
  return 2000 + parseInt(match[1], 10);
}

/** The interval covered by a label, or null if the label does not contain a school year. */
export function schoolYearIntervalFromLabel(label: Maybe<string>): Maybe<Interval> {
  const startYear = parseSchoolYearStartYear(label);
  return startYear === null || startYear === undefined ? null : schoolYearInterval(startYear);
}

/** Whether the school year denoted by `label` is the one running at `at`. */
export function isCurrentSchoolYear(label: Maybe<string>, at: DateTime = DateTime.now()): boolean {
  const startYear = parseSchoolYearStartYear(label);
  return startYear !== null && startYear !== undefined && startYear === schoolYearStartYear(at);
}

/** Whether the school year denoted by `label` already ended before the one running at `at`. */
export function isPastSchoolYear(label: Maybe<string>, at: DateTime = DateTime.now()): boolean {
  const startYear = parseSchoolYearStartYear(label);
  return startYear !== null && startYear !== undefined && startYear < schoolYearStartYear(at);
}

/**
 * The labels from the school year `back` years before the one running at `at` up to and including
 * the one `forward` years after it, oldest first.
 */
export function schoolYearLabelRange(
  prefix: SchoolYearPrefix,
  { back, forward }: { back: number; forward: number },
  at: DateTime = DateTime.now(),
): string[] {
  const current = schoolYearStartYear(at);
  const labels: string[] = [];
  for (let startYear = current - back; startYear <= current + forward; startYear++) {
    labels.push(schoolYearLabelForStartYear(prefix, startYear));
  }
  return labels;
}

/** Sorts school year labels oldest first. Labels without a parseable year sort to the front. */
export function compareSchoolYearLabels(a: Maybe<string>, b: Maybe<string>): number {
  return (parseSchoolYearStartYear(a) ?? -Infinity) - (parseSchoolYearStartYear(b) ?? -Infinity);
}
